import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { subDays } from "date-fns";
import {
  Binoculars,
  CalendarHeart,
  ChevronUp,
  CircleHelp,
  CurlyBracesIcon,
  Download,
  Filter,
  GlobeIcon,
  Search,
  TrendingUp,
} from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { GoogleAdsIcon } from "~/components/icons/google-ads-icon";
import { PageLayout } from "~/components/page-layout";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "~/components/ui/hover-card";
import { Input } from "~/components/ui/input";
import { useSidebar } from "~/components/ui/sidebar";
import { VariantsTable } from "~/components/variants/variants-table";
import {
  isFeatureFlagEnabled,
  useFeatureFlag,
} from "~/instrumentation/feature-flags";
import { trpc } from "~/lib/trpc";
import { cn } from "~/lib/utils";
import { DeletePageButton } from "~/routes/(app)/pages/$pageId/-components/delete-page-button";
import { numberFormatter } from "~/utils/formatters";
import { CreateVariantButton } from "../-components/create-variant-button";
import { DateRangePicker } from "../-components/date-range-picker";

export const Route = createFileRoute("/(app)/pages/$pageId/variants/")({
  component: VariantsPage,
  validateSearch: z.object({
    page: z
      .number()
      .int()
      .gt(0)
      .catch(0)
      .transform((v) => (v === 0 ? undefined : v)),
    pageSize: z
      .number()
      .int()
      .gt(0)
      .lte(50)
      .catch(0)
      .transform((v) => (v === 0 ? undefined : v)),
  }),
  loaderDeps: ({ search: { page, pageSize } }) => ({
    page,
    pageSize,
  }),
  loader: async ({
    deps: { page, pageSize },
    context: { trpc, queryClient },
    params: { pageId },
  }) => {
    localStorage.setItem("lastViewedPageId", pageId);

    await Promise.all([
      queryClient.ensureQueryData(
        trpc.variants.getPaginatedVariants.queryOptions({
          pageId,
          pageSize,
          page,
          shouldGenerateData: isFeatureFlagEnabled("is_demo_account"),
        }),
      ),
      queryClient.ensureQueryData(
        trpc.pages.getMetadata.queryOptions({ id: pageId }),
      ),
    ]);
  },
});

function VariantsPage() {
  const isDemoAccount = useFeatureFlag("is_demo_account");
  const { pageId } = Route.useParams();
  const { page, pageSize } = Route.useLoaderDeps();

  const { data: variantsPage } = useSuspenseQuery(
    trpc.variants.getPaginatedVariants.queryOptions({
      pageId,
      pageSize,
      page,
      shouldGenerateData: isDemoAccount,
    }),
  );
  const { data: pageMetadata } = useSuspenseQuery(
    trpc.pages.getMetadata.queryOptions({ id: pageId }),
  );

  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 30),
    to: new Date(),
  });

  const [totalBaselineVisits, totalPersonalisedVisits] = [
    variantsPage.baseline.totalVisits,
    variantsPage.rows.reduce((prev, curr) => prev + curr.totalVisits, 0),
  ];

  const [totalBaselineConversions, totalPersonalisedConversions] = [
    Math.round(variantsPage.baseline.totalConversions),
    variantsPage.rows.reduce((prev, curr) => prev + curr.conversions.total, 0),
  ];

  const baselineConversionRate =
    Math.round((1000 * totalBaselineConversions) / totalBaselineVisits) / 10;

  const personalisedConversionRate =
    Math.round(
      (1000 * totalPersonalisedConversions) / totalPersonalisedVisits,
    ) / 10;

  const performance =
    Math.round(
      (1000 * (personalisedConversionRate - baselineConversionRate)) /
        baselineConversionRate,
    ) / 10;

  const { open } = useSidebar();
  const gridBreakpoints = open
    ? "lg:grid-cols-2 xl:grid-cols-3"
    : "md:grid-cols-2 lg:grid-cols-3";

  const showEmptyState = variantsPage.rows.length === 0;

  return (
    <>
      <PageLayout.Header className="flex items-center justify-end">
        <DeletePageButton pageId={pageId} pageUrl={pageMetadata.url} />
      </PageLayout.Header>

      <PageLayout.Main>
        <div className="w-full h-full flex flex-col">
          {showEmptyState && (
            <div className="mx-auto max-w-2xl my-auto pb-32">
              <EmptyState
                title="Let's create your first page variant!"
                description="Create your first variant and tell newmode.ai what you want to personalize."
                icons={[
                  () => <GlobeIcon className="text-green-600" />,
                  GoogleAdsIcon,
                  () => <CurlyBracesIcon className="text-yellow-500" />,
                ]}
                action={
                  <CreateVariantButton
                    pageId={pageId}
                    variant="outline"
                    hideIcon
                  />
                }
              />
            </div>
          )}

          {!showEmptyState && (
            <div className="max-w-screen-2xl mx-auto w-full flex flex-col gap-3 md:px-8">
              <div className="flex items-end justify-between">
                <div className="flex gap-3 items-center">
                  <h3>Insights</h3>

                  <div className="h-4 w-px bg-border" />
                  <span className="text-muted-foreground text-sm">
                    Overall performance of all page variants
                  </span>
                </div>

                <DateRangePicker value={dateRange} onChange={setDateRange} />
              </div>

              <div className={`mb-6 grid gap-4 ${gridBreakpoints}`}>
                <Card className="relative !pb-5">
                  <CardContent className="pt-5">
                    <div className="text-sm text-muted-foreground">
                      Unique Visitors
                    </div>
                    <div className="text-2xl font-semibold mt-1">
                      {totalPersonalisedVisits
                        ? numberFormatter.format(totalPersonalisedVisits)
                        : "-"}
                    </div>
                    <div className="absolute right-6 top-6">
                      <div className="p-2 rounded-lg text-indigo-500 border-indigo-100 bg-indigo-50 dark:text-indigo-100 dark:border-indigo-400 dark:bg-indigo-500">
                        <Binoculars size={14} />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="relative !pb-5">
                  <CardContent className="pt-5">
                    <div className="text-sm text-muted-foreground">
                      Conversions
                    </div>
                    <div className="text-2xl font-semibold mt-1">
                      {totalPersonalisedConversions
                        ? numberFormatter.format(totalPersonalisedConversions)
                        : "-"}
                    </div>
                    <div className="absolute right-6 top-6">
                      <div className="p-2 rounded-lg text-pink-500 border-pink-100 bg-pink-50 dark:text-pink-100 dark:border-pink-400 dark:bg-pink-500">
                        <CalendarHeart size={14} />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="relative !pb-5">
                  <CardContent className="flex flex-col h-full pt-5 pb-0">
                    <div className="text-sm text-muted-foreground">
                      Avg. Conversion Rate
                    </div>
                    <div className="text-2xl font-semibold mt-1">
                      {isNaN(personalisedConversionRate)
                        ? "-"
                        : `${numberFormatter.format(personalisedConversionRate)}%`}
                    </div>
                    <div className="absolute right-6 top-6">
                      <div className="p-2 rounded-lg border-green-200 bg-green-100 text-green-600/90">
                        <TrendingUp size={14} />
                      </div>
                    </div>

                    <div className="text-muted-foreground text-base mt-2 flex">
                      <div className="flex items-center gap-2 w-full">
                        {!isNaN(performance) && (
                          <span className="text-green-500 flex items-center gap-1 cursor-default">
                            <ChevronUp size={16} />{" "}
                            {numberFormatter.format(performance)}%
                          </span>
                        )}

                        {!isNaN(performance) && (
                          <HoverCard openDelay={0}>
                            <HoverCardTrigger>
                              <CircleHelp
                                size={16}
                                className="text-gray-400/60"
                              />
                            </HoverCardTrigger>
                            <HoverCardContent className="text-sm w-full max-w-[480px] flex flex-col gap-1.5">
                              {" "}
                              <p>
                                Average conversion rate lift of page variants
                                vs. the original website.
                              </p>
                              <div className="flex flex-col gap-1">
                                <p className="font-semibold">
                                  Original website:
                                </p>
                                <div className="flex items-center gap-4">
                                  <div className="border-r pr-4">
                                    {numberFormatter.format(
                                      totalBaselineVisits,
                                    )}
                                    <br />
                                    <span className="text-muted-foreground">
                                      unique visitors
                                    </span>
                                  </div>
                                  <div className="border-r pr-4">
                                    {numberFormatter.format(
                                      totalBaselineConversions,
                                    )}
                                    <br />
                                    <span className="text-muted-foreground">
                                      conversions
                                    </span>
                                  </div>
                                  <div>
                                    {numberFormatter.format(
                                      baselineConversionRate,
                                    )}
                                    %<br />
                                    <span className="text-muted-foreground">
                                      conversion rate
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </HoverCardContent>
                          </HoverCard>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="flex flex-col gap-3">
                <div
                  className={cn(
                    "flex items-center gap-2 justify-end",
                    isDemoAccount && "justify-between",
                  )}
                >
                  {isDemoAccount && (
                    <div className="flex items-center gap-2">
                      <div className="relative w-full max-w-64">
                        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          className="pl-9"
                          placeholder="Search variants..."
                        />
                      </div>
                      <Button variant="outline" size="icon">
                        <Filter size={14} />
                      </Button>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    {isDemoAccount && (
                      <Button variant="outline">
                        <Download size={14} className="text-muted-foreground" />
                        Export
                      </Button>
                    )}
                    <CreateVariantButton pageId={pageId} />
                  </div>
                </div>

                <VariantsTable tableData={variantsPage} />
              </div>
            </div>
          )}
        </div>
      </PageLayout.Main>
    </>
  );
}
